
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Loader2, ChevronLeft, Play, Trash2 } from 'lucide-react';
import { HighlightItem, Language } from '../../types';
import { fetchGlobalHighlights, deleteHighlight } from '../../services/highlightService';
import { useAuth } from '../../context/AuthContext';
import { getLeagueName } from '../../services/leagueService';
import { getTeamName } from '../../services/teamService';
import { useVideo } from '../../context/VideoContext';
import { t } from '../../i18n';
import SEO from '../Common/SEO';
import { ConfirmModal } from '../Common/ConfirmModal';

const LEAGUE_COLORS: Record<string, string> = {
  'premier league': 'bg-purple-600',
  laliga: 'bg-rose-600',
  'serie a': 'bg-blue-600',
  bundesliga: 'bg-red-600',
  'ligue 1': 'bg-emerald-500',
  'champions league': 'bg-indigo-900',
  'europa league': 'bg-orange-500',
  eredivisie: 'bg-red-500',
  'primeira liga': 'bg-indigo-600',
  mls: 'bg-blue-800',
  'k league': 'bg-rose-700',
};


export const HighlightsView: React.FC<{ lang: Language }> = ({ lang }) => {
  const [allHighlights, setAllHighlights] = useState<HighlightItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLeagueId, setActiveLeagueId] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<HighlightItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchGlobalHighlights(true, { limit: 1000 });
      setAllHighlights(data);
    } catch (error) {
      console.error('Highlights load failed', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const leagues = useMemo(() => {
    const leagueMap = new Map<string, HighlightItem[]>();
    allHighlights.forEach(h => {
      const key = h.league_id || h.competition.trim().toLowerCase();
      if (!leagueMap.has(key)) leagueMap.set(key, []);
      leagueMap.get(key)?.push(h);
    });

    return Array.from(leagueMap.entries())
      .map(([id, items]) => {
        const firstItem = items[0];
        const name = firstItem.league_id
          ? getLeagueName(parseInt(firstItem.league_id), lang, firstItem.competition)
          : firstItem.competition;
        const colorKey = Object.keys(LEAGUE_COLORS).find(k => id.toLowerCase().includes(k));
        const sortedItems = [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const latestDate = new Date(sortedItems[0]?.date ?? 0).getTime();
        return { id, name, items: sortedItems, latestDate, color: colorKey ? LEAGUE_COLORS[colorKey] : 'bg-slate-400' };
      })
      .sort((a, b) => b.latestDate - a.latestDate);
  }, [allHighlights, lang]);

  // Set initial active league
  useEffect(() => {
    if (leagues.length > 0 && !activeLeagueId) {
      setActiveLeagueId(leagues[0].id);
    }
  }, [leagues, activeLeagueId]);

  const activeLeague = useMemo(
    () => leagues.find(l => l.id === activeLeagueId) ?? leagues[0],
    [leagues, activeLeagueId]
  );

  const groupedByDate = useMemo(() => {
    if (!activeLeague) return [];
    const groups: Record<string, HighlightItem[]> = {};
    activeLeague.items.forEach(h => {
      const dateStr = h.date.split('T')[0];
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(h);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, items]) => ({
        date,
        formattedDate: new Date(date + 'T12:00:00').toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', {
          year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
        }),
        items: items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      }));
  }, [activeLeague, lang]);

  const { setActiveVideo } = useVideo();

  const handleSelectMatch = (item: HighlightItem) => {
    if (!item.match_id) return;
    const video = item.videos?.[0];
    if (video) {
      const m = video.embed.match(/(?:embed\/|v=)([a-zA-Z0-9_-]{11})/);
      const videoId = m ? m[1] : null;
      if (videoId) setActiveVideo({ videoId, title: item.title, matchId: item.match_id, isPaused: false });
    }
    window.navigateTo(`/game/${item.match_id}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, item: HighlightItem) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const { data } = await (await import('../../services/authService')).supabase
        .from('match_highlights').select('id').eq('embed_id', itemToDelete.embed_id).single();
      if (data?.id) { await deleteHighlight(data.id); await load(); }
    } catch { alert('Failed to delete highlight'); }
    finally { setIsDeleting(false); setDeleteModalOpen(false); setItemToDelete(null); }
  };

  const isWorldCup = (id: string) => id === '1';
  const getLogoSrc = (id: string) =>
    isWorldCup(id) ? '/assets/fiwc.png' : `https://media.api-sports.io/football/leagues/${id}.png`;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-rose-500" size={32} />
        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t(lang, 'syncingMarketIntel')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-32">
      <SEO
        title={t(lang, 'matchHighlights')}
        description={lang === 'ko' ? '주요 리그의 축구 경기 하이라이트 영상을 한눈에 확인하세요.' : 'Check out football match highlights from major leagues at a glance.'}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100/50 px-6 h-16 flex items-center gap-4">
        <button onClick={() => window.history.back()} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full bg-slate-50 text-slate-900 md:hover:bg-slate-100 transition-all active:scale-90">
          <ChevronLeft size={22} strokeWidth={2.5} />
        </button>
        <div className="flex flex-col min-w-0">
          <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'matchHighlights')}</h1>
          {activeLeague && (
            <span className="text-[10px] font-bold text-rose-500 uppercase tracking-[0.2em] mt-0.5 truncate">{activeLeague.name}</span>
          )}
        </div>
      </header>

      {/* League Tabs */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100">
        <div className="flex flex-wrap gap-2 px-4 py-3">
          {leagues.map(league => {
            const isActive = league.id === activeLeagueId;
            return (
              <button
                key={league.id}
                onClick={() => setActiveLeagueId(league.id)}
                className={`flex items-center gap-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border whitespace-nowrap active:scale-95 ${
                  isActive
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm px-3 py-1.5'
                    : 'bg-white text-slate-500 border-slate-200/60 md:hover:border-slate-400 p-1.5'
                }`}
              >
                {!isNaN(parseInt(league.id)) ? (
                  <img
                    src={getLogoSrc(league.id)}
                    alt={league.name}
                    className={`object-contain flex-shrink-0 ${isActive ? 'w-4 h-4' : 'w-5 h-5 opacity-70'}`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className={`rounded-full flex-shrink-0 ${isActive ? 'w-1.5 h-1.5 bg-rose-400' : 'w-5 h-5 ' + league.color + ' opacity-60'}`} />
                )}
                {isActive && <span>{league.name}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content — active league grouped by date */}
      <div className="p-6 md:p-8 lg:p-12 space-y-16">
        {groupedByDate.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 opacity-30 gap-4">
            <Play size={48} strokeWidth={1} />
            <p className="text-sm font-black uppercase tracking-widest">{t(lang, 'noHighlights') || 'No highlights yet'}</p>
          </div>
        ) : groupedByDate.map(group => (
          <section key={group.date} className="space-y-6">
            {/* Date header */}
            <div className="flex items-center gap-3 px-1">
              <div className={`w-1 h-6 rounded-full ${activeLeague?.color ?? 'bg-slate-300'}`} />
              <h2 className="text-base font-black text-slate-900 uppercase tracking-tight leading-none">{group.formattedDate}</h2>
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{group.items.length} {t(lang, 'matches')}</span>
            </div>

            {/* Horizontal card scroll */}
            <div
              ref={el => { cardRefs.current[group.date] = el; }}
              className="flex overflow-x-auto no-scrollbar gap-6 pb-4 snap-x cursor-grab active:cursor-grabbing select-none"
              onMouseDown={(e) => {
                const container = cardRefs.current[group.date];
                if (!container) return;
                setIsDragging(false);
                let isDown = true;
                let startX = e.pageX - container.offsetLeft;
                let scrollLeft = container.scrollLeft;
                const onMove = (ev: MouseEvent) => {
                  if (!isDown) return;
                  ev.preventDefault();
                  const x = ev.pageX - container.offsetLeft;
                  if (Math.abs(x - startX) > 5) setIsDragging(true);
                  container.scrollLeft = scrollLeft - (x - startX) * 2;
                };
                const onUp = () => { isDown = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
              onClickCapture={(e) => { if (isDragging) e.stopPropagation(); }}
            >
              {group.items.map((item, idx) => (
                <div key={`${item.match_id}-${idx}`} className="flex flex-col flex-shrink-0 w-[280px] md:w-[320px] snap-center">
                  <div
                    onClick={() => !isDragging && handleSelectMatch(item)}
                    className="group bg-white rounded-[28px] overflow-hidden border border-gray-100/80 transition-all duration-300 cursor-pointer relative"
                  >
                    <div className="aspect-video relative overflow-hidden bg-slate-900">
                      <img
                        src={item.thumbnail}
                        alt=""
                        className="w-full h-full object-cover opacity-90 transition-opacity group-active:opacity-100"
                        onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=800'; }}
                      />
                      <div className="absolute top-4 left-4">
                        <div className="px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${activeLeague?.color ?? 'bg-slate-400'}`} />
                          <span className="text-[9px] font-black text-white uppercase tracking-widest">
                            {new Date(item.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/30 transition-transform active:scale-90">
                          <Play size={20} fill="currentColor" className="ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 p-3">
                    <h3 className="text-sm font-black text-black uppercase tracking-tight line-clamp-2 leading-tight">
                      {item.home_team_id && item.away_team_id
                        ? `${getTeamName(item.home_team_id, lang, item.home_team)} vs ${getTeamName(item.away_team_id, lang, item.away_team)}`
                        : item.title}
                    </h3>
                    {isAdmin && (
                      <button
                        onClick={(e) => handleDeleteClick(e, item)}
                        className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-500 transition-all active:scale-90"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => !isDeleting && setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={t(lang, 'confirmDelete')}
        confirmLabel={t(lang, 'delete')}
        cancelLabel={t(lang, 'cancel')}
        isLoading={isDeleting}
      />
    </div>
  );
};
